import { MatchingProfilePreviewCard } from "@/components/mentor-profile/matching-profile-preview-card";
import { PhoneFrame } from "@/components/mentor-profile/phone-frame";
import { useToast } from "@/components/ui/use-toast";
import {
  MENTOR_PROFILE_REVIEWS_QUERY_KEY,
  useMentorProfileReviewByMentorId,
  useSendMentorInvite,
  type MentorProfileDraft,
} from "@/lib/api/mentor-profile";
import { useGetUserSupervisorAndChannel, type User } from "@/lib/api/user";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import { getMentorInviteBlockers } from "@/lib/mentor-profile/invite-readiness";
import { useMentorProfileEnabled } from "@/lib/hooks/use-mentor-profile";
import { isMentorIdentity } from "@/lib/utils/system-roles";
import { Button, Skeleton } from "@somethings-ui/web";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { DateTime } from "luxon";
import { liveProfileDraft } from "./matching-profile-source";
import { RecordProfileMatchCard } from "./record-profile-match-card";

interface RecordMatchingProfileTabProps {
  user: User;
}

function ProfileInviteBar({
  user,
  inviteSentAt,
  statusKnown,
}: {
  user: User;
  inviteSentAt: string | null | undefined;
  statusKnown: boolean;
}) {
  const ability = useAbility();
  const mentorProfileEnabled = useMentorProfileEnabled();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const sendInvite = useSendMentorInvite();
  const canSend =
    mentorProfileEnabled &&
    hasPrivilegedAccessFromAbility(ability) &&
    ability.can("update", "users") &&
    isMentorIdentity(user.systemRoles);
  const { data: supervisorAndChannel, isPending: supervisorPending } =
    useGetUserSupervisorAndChannel(user.id, canSend);
  const sentAt = inviteSentAt
    ? DateTime.fromISO(inviteSentAt).toFormat("MMMM d, yyyy")
    : null;

  const handleSend = () => {
    const blockers = getMentorInviteBlockers(user, {
      hasLeadMentor: Boolean(supervisorAndChannel?.supervisor),
    });
    if (blockers.length > 0) {
      toast({
        title:
          "Onboarding email cannot be sent until the following fields are filled:",
        description: (
          <ul className="mt-2 list-disc pl-4">
            {blockers.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ),
        variant: "destructive",
      });
      return;
    }

    sendInvite.mutate(
      { userId: user.id },
      {
        onSuccess: async ({ inviteUrl }) => {
          await queryClient.invalidateQueries({
            queryKey: [
              ...MENTOR_PROFILE_REVIEWS_QUERY_KEY,
              "by-mentor",
              user.id,
            ],
          });
          try {
            await navigator.clipboard.writeText(inviteUrl);
            toast({
              title: "Onboarding invite ready",
              description: "Invite link copied to clipboard.",
            });
          } catch {
            toast({
              title: "Onboarding invite ready",
              description: inviteUrl,
            });
          }
        },
        onError: () => {
          toast({
            title: "Couldn't generate invite",
            description: "Please try again.",
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <div className="mb-6 flex items-center justify-between rounded-2xl border border-[rgba(31,30,29,0.08)] px-6 py-3.5">
      <p className="font-body text-xs font-medium leading-4 text-[#58534b]">
        {!statusKnown
          ? "Invite status unavailable"
          : sentAt
            ? `Last invited ${sentAt}`
            : "Invite not sent"}
      </p>
      {canSend ? (
        <Button
          size="sm"
          variant="secondary"
          fullWidth={false}
          className="min-w-[120px]"
          loading={sendInvite.isPending}
          disabled={sendInvite.isPending || supervisorPending}
          onPress={handleSend}
        >
          Send Email to Create Profile
        </Button>
      ) : null}
    </div>
  );
}

export function RecordMatchingProfileTab({
  user,
}: RecordMatchingProfileTabProps) {
  const { data: review, isPending, isError, error } =
    useMentorProfileReviewByMentorId(user.id);
  const isNotFound = isAxiosError(error) && error.response?.status === 404;
  const liveProfile = liveProfileDraft(user);
  const pendingReview =
    !isNotFound &&
    review &&
    (review.status === "submitted" || review.status === "in_review")
      ? review
      : null;

  if (isPending) {
    return <Skeleton className="h-96 w-full" />;
  }

  return (
    <section className="w-full max-w-[1100px]">
      {user.has_onboarded === false ? (
        <ProfileInviteBar
          user={user}
          inviteSentAt={isNotFound ? null : review?.invite_sent_at}
          statusKnown={!isError || isNotFound}
        />
      ) : null}
      {pendingReview ? (
        <div className="mb-6 flex items-center justify-between gap-4 rounded-2xl bg-[#efe4cf] px-4 py-3">
          <p className="font-body text-sm font-medium leading-5 text-[#1f1e1d]">
            New profile updates ready for review
          </p>
          <Link
            to="/users/mentor-profile-reviews/$reviewId"
            params={{ reviewId: pendingReview.id }}
            className="inline-flex h-9 shrink-0 items-center rounded-full bg-[#1f1e1d] px-4 font-body text-sm font-medium text-white"
          >
            Review
          </Link>
        </div>
      ) : null}
      <ProfilePreview
        heading="Live matching profile"
        description="Saved information below is live to mentees"
        user={user}
        profile={liveProfile}
      />
      {isError && !isNotFound ? (
        <p className="mt-3 font-body text-sm text-text-secondary">
          Draft status is unavailable. Showing the live profile.
        </p>
      ) : null}
    </section>
  );
}

function ProfilePreview({
  heading,
  description,
  user,
  profile,
  readOnly = false,
}: {
  heading: string;
  description: string;
  user: User;
  profile: MentorProfileDraft;
  readOnly?: boolean;
}) {
  return (
    <div className="mt-6">
      <h3 className="font-heading text-sm font-bold leading-5 text-text-primary">
        {heading}
      </h3>
      <p className="mt-1 font-body text-xs font-medium leading-4 text-[#6e6b67]">
        {description}
      </p>
      <div className="mt-3 flex flex-col items-start gap-[22px] xl:flex-row">
        <RecordProfileMatchCard
          user={user}
          profile={profile}
          readOnly={readOnly}
        />
        <div className="relative h-[600px] w-[310px] shrink-0">
          <div className="absolute left-5 top-0 origin-top-left scale-75">
            <PhoneFrame>
              <MatchingProfilePreviewCard draft={profile} />
            </PhoneFrame>
          </div>
        </div>
      </div>
    </div>
  );
}
